import { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, FieldValues, PathValue, Path } from 'react-hook-form'
import classNames from 'classnames'
import Select, { InputActionMeta } from 'react-select'
import Creatable from 'react-select/creatable'

import { t } from '@/i18n'
import { useApp } from '@/hooks'
import { InfiniteLoader } from '../'
import { OptionSelectorProps } from './types'
import { FormError } from '.'

const OptionSelector = <T extends FieldValues>({
  className,
  label,
  name,
  required,
  options,
  control,
  emptyValue,
  isSearchable,
  isAsync,
  optionsLoading,
  fieldValue,
  isMenuOpen,
  isEditing,
  maxHeight = '300px',
  fetchMoreStatus,
  creatable = false,
  onCreateOption,
  onFetchMore,
  onFieldChange,
  onSearch,
  ...rest
}: OptionSelectorProps<HTMLSelectElement, T>) => {
  const [focused, setFocused] = useState<boolean>(false)
  const { isRTL } = useApp()
  const [search, setSearch] = useState<string | undefined>(undefined)
  const timer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    if (!isAsync) return
    const debounce = search ? 300 : 0
    if (search !== undefined) {
      timer.current = setTimeout(() => {
        onSearch?.(search)
      }, debounce)
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [isAsync, onSearch, search])

  const renderField = useCallback(
    (value: PathValue<T, Path<T>>, onChange: any) => {
      if ([undefined, null, ''].includes(value)) {
        if (emptyValue) {
          return <div className="empty-value">{emptyValue}</div>
        }
      }

      const selectedValue =
        options?.find(
          ({ value: v, label: l }) =>
            v === value ||
            v === fieldValue ||
            v === value?.id ||
            l === value?.name,
        ) ?? null

      if (!selectedValue && emptyValue) {
        return <div className="empty-value">{emptyValue}</div>
      }
      const Component = creatable ? Creatable : Select
      return (
        <Component
          onCreateOption={onCreateOption}
          className={classNames('re-select', { focused })}
          options={options}
          noOptionsMessage={() =>
            optionsLoading ? t('loading') : 'No options'
          }
          placeholder={
            optionsLoading
              ? options?.length
                ? ''
                : t('loading')
              : rest.placeholder
          }
          value={selectedValue}
          isDisabled={rest.disabled}
          isClearable={true}
          isMulti={false}
          isRtl={isRTL}
          menuPosition="fixed"
          closeMenuOnScroll
          menuShouldScrollIntoView={false}
          menuIsOpen={isMenuOpen}
          menuPlacement={rest.menuPlacement || 'auto'}
          isSearchable={isSearchable}
          inputValue={focused ? search : ''}
          onInputChange={(s: string, { action }: InputActionMeta) => {
            if (action === 'input-change') {
              setSearch(s)
            } else if (action === 'menu-close' && !options?.length) {
              setSearch(s)
            }
          }}
          onMenuOpen={() => setFocused(true)}
          onMenuClose={() => setFocused(false)}
          {...(onSearch ? { filterOption: () => true } : {})}
          components={{
            MenuList: ({ ...props }) => (
              <InfiniteLoader
                maxHeight={maxHeight}
                fetchMoreStatus={!!search && !isAsync ? 'end' : fetchMoreStatus}
                onFetchMore={() =>
                  !search || isAsync ? onFetchMore?.() : undefined
                }
              >
                {props.children}
              </InfiniteLoader>
            ),
          }}
          onChange={v => {
            onChange(v?.value)
            onFieldChange?.(v?.value)
          }}
        />
      )
    },
    [
      creatable,
      onCreateOption,
      emptyValue,
      fetchMoreStatus,
      fieldValue,
      focused,
      isAsync,
      isMenuOpen,
      isRTL,
      isSearchable,
      maxHeight,
      onFetchMore,
      onFieldChange,
      onSearch,
      options,
      optionsLoading,
      rest.disabled,
      rest.menuPlacement,
      rest.placeholder,
      search,
    ],
  )

  if (!name) return null

  return (
    <label className={className}>
      {!!label && (
        <span className="name">
          {label}
          {!!required && <sup>*</sup>}
        </span>
      )}
      <span className={classNames('select', { focused })}>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              {renderField(value, onChange)}
              {isEditing && <FormError error={error?.message} />}
            </>
          )}
        />
      </span>
    </label>
  )
}

export default OptionSelector
